'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import {ProductCard} from '@/components/products/ProductCard'
import type {ProductDocument} from '@/types/sanity'

type ProductCarouselItem = {
  product: ProductDocument
  href: string
}

type ProductsCarouselClientProps = {
  items: ProductCarouselItem[]
  locale?: string
  cardsPerView?: number
  marketKey?: string
  hideProductPrices?: boolean
}

export function ProductsCarouselClient({
  items,
  locale,
  cardsPerView = 3,
  marketKey,
  hideProductPrices = false,
}: ProductsCarouselClientProps) {
  if (!items.length) return null

  const normalizedCardsPerView = Math.min(4, Math.max(1, cardsPerView))
  const itemBasisClass = carouselItemBasisClass(normalizedCardsPerView)

  return (
    <Carousel
      opts={{
        align: 'start',
        slidesToScroll: 1,
        containScroll: 'trimSnaps',
      }}
      className="relative"
    >
      <CarouselContent>
        {items.map((item, index) => (
          <CarouselItem
            key={`${item.product._id || item.href}-${index}`}
            className={itemBasisClass}
          >
            <ProductCard
              product={item.product}
              href={item.href}
              locale={locale}
              marketKey={marketKey}
              hideProductPrices={hideProductPrices}
              equalizeContentHeight
              className="h-full shadow-[0px_1px_6px_rgba(0,0,0,0.12)] hover:shadow-[0px_2px_10px_rgba(0,0,0,0.16)]"
            />
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious className="top-1/2 left-2 -translate-y-1/2 md:-left-12 md:left-auto" />
      <CarouselNext className="top-1/2 right-2 -translate-y-1/2 md:-right-12" />
    </Carousel>
  )
}

function carouselItemBasisClass(cardsPerView: number): string {
  if (cardsPerView <= 1) return 'basis-full'
  if (cardsPerView === 2) return 'basis-full md:basis-1/2'
  if (cardsPerView === 4) return 'basis-full md:basis-1/2 lg:basis-1/4'
  return 'basis-full md:basis-1/2 lg:basis-1/3'
}
